import { useState } from "react";
import { LogOut, Menu } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { AllisonOrb } from "@/components/alia/allison-orb";
import { ConfirmLogoutDialog } from "@/components/alia/confirm-logout-dialog";
import { Logo } from "@/components/alia/logo";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";

interface MobileNavbarProps {
  /** Callback al click del botón hamburguesa */
  onMenuClick: () => void;
  /** Título de la página actual (opcional, sino muestra logo) */
  title?: string;
  /** Acciones adicionales a la derecha (botones, indicadores, etc) */
  rightSlot?: React.ReactNode;
  /** Mostrar indicador de Allison activa */
  showAllisonStatus?: boolean;
}

/**
 * MobileNavbar — top bar para viewports < lg.
 * Solo se renderiza en mobile/tablet (`lg:hidden`).
 *
 * Estructura:
 *   [☰]   Logo o Título   [acciones] [Allison status]
 *
 * El botón hamburguesa abre el sidebar drawer. Cuando el usuario tap fuera
 * del sidebar abierto, se cierra (ese comportamiento vive en sidebar.tsx).
 *
 * Uso:
 *   <MobileNavbar
 *     onMenuClick={() => setSidebarOpen(true)}
 *     title="Inbox"
 *     rightSlot={<RefreshIndicator ... />}
 *   />
 */
export function MobileNavbar({
  onMenuClick,
  title,
  rightSlot,
  showAllisonStatus = true,
}: MobileNavbarProps) {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);

  function handleConfirmLogout() {
    setShowConfirm(false);
    signOut();
    navigate("/login", { replace: true });
  }

  return (
    <>
    <header
      className={cn(
        "sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-border bg-surface/95 px-3 backdrop-blur-md",
        "lg:hidden"  // solo visible en mobile/tablet
      )}
    >
      {/* Botón hamburguesa - tamaño tactil 44x44 */}
      <button
        onClick={onMenuClick}
        className="flex size-10 shrink-0 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-surface-2 active:bg-surface-2"
        aria-label="Abrir menú"
      >
        <Menu className="size-5" />
      </button>

      {/* Logo o título */}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {title ? (
          <h1 className="truncate text-base font-semibold text-foreground">
            {title}
          </h1>
        ) : (
          <Logo size={22} showWordmark />
        )}
      </div>

      {/* Allison status (solo si showAllisonStatus=true) */}
      {showAllisonStatus && (
        <div className="flex items-center gap-1.5 rounded-full border border-brand/20 bg-brand/[0.05] px-2 py-1">
          <AllisonOrb size={14} active />
          <span className="text-[11px] font-medium text-brand">Activa</span>
        </div>
      )}

      {/* Slot derecho para acciones contextuales de la página */}
      {rightSlot && (
        <div className="flex shrink-0 items-center gap-1.5">{rightSlot}</div>
      )}

      {session && (
        <button
          onClick={() => setShowConfirm(true)}
          className="flex size-10 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-surface-2 hover:text-foreground active:bg-surface-2"
          aria-label="Cerrar sesión"
        >
          <LogOut className="size-5" />
        </button>
      )}
    </header>

    <ConfirmLogoutDialog
      open={showConfirm}
      onCancel={() => setShowConfirm(false)}
      onConfirm={handleConfirmLogout}
    />
    </>
  );
}
